import { casesOf } from '../../../../data/order-item';
import { isActiveProduct, tierPrice } from '../../../../data/product';
import type { ProductOrderItem } from '../data/product-order-item';
import type { Family } from '../hooks/use-families';

export type FamilyTotals = {
  totalCases: number;
  savingsDollars: number;
  savingsPercent: number;
  extendedDollars: number;
  /** Frontline (current_cost) extended — used for cross-family weighted avg in aggregateCommitted. */
  frontlineExtended: number;
  minDOH: number | null;
};

/**
 * Aggregates billed at the family's tier (NY deals roll up across the family,
 * so every member uses the same tier price regardless of its own quantity).
 *
 * `getCases` lets the caller swap committed cases (`casesOf`) for projected
 * cases (`casesSold(product, 90)`) — same math powers committed totals AND
 * the forward-looking priority score. Defaults to `casesOf` for the common
 * committed-allocation case.
 *
 * `minDOH` is the smallest `days_on_hand` across active members — inactive
 * members are skipped because their DOH is either undefined or computed as
 * 0 (no sales → "0 days left"), which would falsely flag the family as urgent.
 */
export function familyTotals(
  items: ProductOrderItem[],
  getCases: (item: ProductOrderItem) => number = casesOf
): FamilyTotals {
  // tierPrice is family-wide, so we need totalCases before computing any
  // per-member extended/savings figure. Everything else folds into pass two.
  let totalCases = 0;
  for (const item of items) {
    totalCases += getCases(item);
  }

  let minDOH: number | null = null;
  let savings = 0;
  let frontlineExtended = 0;
  let extended = 0;
  for (const item of items) {
    const { product } = item;

    if (isActiveProduct(product)) {
      const doh = product.stats?.days_on_hand;
      if (Number.isFinite(doh) && (minDOH === null || doh < minDOH)) {
        minDOH = Math.max(0, doh);
      }
    }

    const cases = getCases(item);
    if (cases === 0) {
      continue;
    }

    const perCasePrice = tierPrice(product, totalCases);
    extended += cases * perCasePrice;

    const frontline = product.pricing?.current_cost ?? 0;
    if (frontline > 0) {
      frontlineExtended += cases * frontline;
      const perCaseSavings = frontline - perCasePrice;
      if (perCaseSavings > 0) {
        savings += cases * perCaseSavings;
      }
    }
  }

  const percent = frontlineExtended > 0 ? (savings / frontlineExtended) * 100 : 0;

  return {
    totalCases,
    savingsDollars: savings,
    savingsPercent: percent,
    extendedDollars: extended,
    frontlineExtended,
    minDOH,
  };
}

export type DealsAggregates = {
  cashDeployed: number;
  totalSavings: number;
  avgSavingsPct: number;
  totalCases: number;
  familiesCommittedCount: number;
};

/**
 * Cross-family rollup of committed totals — drives the deals summary banner.
 * Reads pre-computed `family.totals` so this function does no per-member
 * iteration; the heavy lifting already happened in `familyTotals`.
 */
export function aggregateCommitted(families: Family[]): DealsAggregates {
  let cashDeployed = 0;
  let totalSavings = 0;
  let frontlineExtended = 0;
  let totalCases = 0;
  let familiesCommittedCount = 0;

  for (const family of families) {
    if (family.totals.totalCases === 0) {
      continue;
    }

    familiesCommittedCount += 1;
    cashDeployed += family.totals.extendedDollars;
    totalSavings += family.totals.savingsDollars;
    frontlineExtended += family.totals.frontlineExtended;
    totalCases += family.totals.totalCases;
  }

  const avgSavingsPct = frontlineExtended > 0 ? (totalSavings / frontlineExtended) * 100 : 0;

  return { cashDeployed, totalSavings, avgSavingsPct, totalCases, familiesCommittedCount };
}
